import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  Grid, 
  Button, 
  CircularProgress, 
  Divider, 
  Chip, 
  Card, 
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  LinearProgress,
  Tab,
  Tabs,
  Alert,
  Stack,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  ListItemIcon,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  Event as EventIcon,
  People as PeopleIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Info as InfoIcon,
  QrCode as QrCodeIcon,
  Share as ShareIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as HiddenIcon,
  ContentCopy as ContentCopyIcon,
  LocalActivity as TicketIcon,
  MonetizationOn as MonetizationIcon,
  CheckCircle as CheckInIcon,
  ReceiptLong as ReceiptIcon,
  Speed as SpeedIcon,
  Calculate as CalculateIcon,
  Add as AddIcon,
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
  FormatListBulleted as ListIcon,
  Payment as PaymentIcon,
  AttachMoney as MoneyIcon,
  Assessment as AnalyticsIcon,
  Campaign as MarketingIcon,
  PersonAdd as InviteIcon,
  Print as PrintIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Timeline as TimelineIcon,
  BubbleChart as BubbleChartIcon,
  Chat as ChatIcon,
  Image as ImageIcon,
  Campaign as CampaignIcon,
  GridView as GridViewIcon,
  AutoAwesome as AutoAwesomeIcon,
  DateRange as DateRangeIcon,
  Code as CodeIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrganization } from '../../../contexts/OrganizationContext';
import EventDetailPage from '../../Events/EventDetail';
import { collection, doc, getDoc, updateDoc, serverTimestamp, addDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { nanoid } from 'nanoid';
import TestDataGenerator from '../../../components/TestDataGenerator';
import ShareDialog from './components/ShareDialog';
import ShareAnalytics from './components/ShareAnalytics';
import EventQRCode from '../../../components/QRCode/EventQRCode';
import qrCodeService from '../../../services/qrCodeService';
import QRCodeModal from '../../../components/QRCode/QRCodeModal';
import OrganizationMarketingTab from '../Marketing/OrganizationMarketingTab';
import CampaignsTable from '../Marketing/CampaignsTable';
import visualAssetService, { DesignStyle, AssetType } from '../../../services/visualAssetService';
import EventAssetsTable from './components/EventAssetsTable';
import EventAnalyticsTab from './components/EventAnalyticsTab';
import EventEmbedGenerator from '../../../components/events/EventEmbedGenerator';

interface LocationType {
  name?: string;
  address?: string;
  coordinates?: [number, number];
}

export interface ShareClick {
  id: string;
  platform: 'facebook' | 'linkedin' | 'instagram' | 'direct';
  timestamp: Date;
  referrer: string;
  userAgent: string;
  sharedBy?: {
    userId: string;
    displayName: string;
    email: string;
  };
  uniqueShareId: string;
  clicks: number;
  sales: number;
  revenue: number;
}

export interface ShareStats {
  facebook: number;
  linkedin: number;
  instagram: number;
  direct: number;
  total: number;
  totalClicks: number;
  totalSales: number;
  totalRevenue: number;
  clickThroughRate: number;
  conversionRate: number;
}

interface Expense {
  id: string;
  description: string;
  amount: number;
  date: Date;
}

interface EventType {
   id: string;
   title: string;
   description: string;
   eventType: string;
   location: string | LocationType;
   startDate: Date;
   endDate: Date;
   isPaid: boolean;
   price: number;
   maxCapacity: number;
   ticketsSold: number;
   coverPhotoUrl?: string;
   attendees: Array<{
      id: string;
      name: string;
      email: string;
      ticketType: string;
      purchaseDate: Date;
      checkInTime?: Date;
   }>;
   revenue: number;
   publicLinkId?: string;
   isPublic: boolean;
   lastUpdated?: Date;
   ticketTypes?: Array<{
      id: string;
      name: string;
      description?: string;
      price: number;
      capacity: number;
      ticketsSold: number;
      saleStartDate?: Date;
      saleEndDate?: Date;
      isVisible: boolean;
   }>;
   shareClicks?: ShareClick[];
   shareStats?: ShareStats;
   expenses?: Expense[];
   totalExpenses?: number;
}

interface QRCodeStats {
  totalScans: number;
  lastScan: Date | null;
  created: Date;
}

// Add TabPanel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`event-tabpanel-${index}`}
      aria-labelledby={`event-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

// Add ShareAnalytics interface if not already defined
interface ShareAnalyticsProps {
  shareClicks: ShareClick[];
  shareStats: ShareStats;
}

interface TabDefinition {
  label: string;
  icon: React.ReactElement;
  content: React.ReactNode;
}

function a11yProps(index: number) {
  return {
    id: `event-tab-${index}`,
    'aria-controls': `event-tabpanel-${index}`,
  };
}

const OrganizationEventDetail: React.FC = () => {
   const { id, eventId } = useParams<{ id: string; eventId: string }>();
   const navigate = useNavigate();
   const { activeOrganization } = useOrganization();

   const [event, setEvent] = useState<EventType | null>(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [tabValue, setTabValue] = useState(0);
   const [shareDialogOpen, setShareDialogOpen] = useState(false);
   const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
   const [deleteConfirmText, setDeleteConfirmText] = useState('');
   const [publicLink, setPublicLink] = useState<string | null>(null);
   const [generatingLink, setGeneratingLink] = useState(false);
   const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
   const [qrCodeStats, setQRCodeStats] = useState<QRCodeStats | null>(null);
   const [loadingStats, setLoadingStats] = useState(false);
   const [qrCodeModalOpen, setQrCodeModalOpen] = useState(false);
   const [expenses, setExpenses] = useState<Expense[]>([]);
   const [newExpense, setNewExpense] = useState({ description: '', amount: '' });
   const [totalExpenses, setTotalExpenses] = useState(0);
   const [previewOpen, setPreviewOpen] = useState(false);
   const [marketingSubTabValue, setMarketingSubTabValue] = useState(0);
   const [campaignCreatorOpen, setCampaignCreatorOpen] = useState(false);
   const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
   const [bannerDescription, setBannerDescription] = useState('');
   const [selectedBanner, setSelectedBanner] = useState<{
      id: string;
      title: string;
      description: string;
      imageUrl: string;
   } | null>(null);
   const [generatingImage, setGeneratingImage] = useState(false);
   const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
   const [imagePrompt, setImagePrompt] = useState('');
   const [currentAssetType, setCurrentAssetType] = useState<AssetType>('banner');
   const [imageStyle, setImageStyle] = useState<DesignStyle>('modern');
   const [embedCodeModalOpen, setEmbedCodeModalOpen] = useState(false);

   useEffect(() => {
      if (id && eventId) {
         fetchEventDetails();
      }
   }, [id, eventId]);

   useEffect(() => {
      if (eventId) {
         fetchQRCodeStats();
      }
   }, [eventId]);

   useEffect(() => {
      if (expenses) {
         const total = expenses.reduce((sum, expense) => sum + expense.amount, 0);
         setTotalExpenses(total);
      }
   }, [expenses]);

   const fetchEventDetails = async () => {
      if (!id || !eventId) {
         setLoading(false);
         return;
      }

      setLoading(true);
      setError(null);

      try {
         const eventRef = doc(db, 'events', eventId);
         const eventSnapshot = await getDoc(eventRef);

         if (eventSnapshot.exists()) {
            const eventData = eventSnapshot.data();

            try {
               // Safely parse dates
               const startDate = eventData.startDate
                  ? eventData.startDate instanceof Date
                     ? eventData.startDate
                     : new Date(
                          eventData.startDate.seconds
                             ? eventData.startDate.seconds * 1000
                             : eventData.startDate
                       )
                  : new Date();

               const endDate = eventData.endDate
                  ? eventData.endDate instanceof Date
                     ? eventData.endDate
                     : new Date(
                          eventData.endDate.seconds
                             ? eventData.endDate.seconds * 1000
                             : eventData.endDate
                       )
                  : new Date();

               setEvent({
                  ...eventData,
                  id: eventSnapshot.id,
                  ticketsSold: eventData.ticketsSold || 0,
                  revenue: eventData.revenue || 0,
                  maxCapacity: eventData.maxCapacity || 100,
                  startDate,
                  endDate,
                  attendees: eventData.attendees || [],
                  ticketTypes: eventData.ticketTypes || [],
                  expenses: eventData.expenses || [],
                  totalExpenses: eventData.totalExpenses || 0,
               } as EventType);
            } catch (dateErr) {
               setError('Failed to parse event dates. Please try again.');
            }

            // Set public link if it exists
            if (eventData.publicLinkId) {
               const baseUrl = window.location.origin;
               setPublicLink(`${baseUrl}/events/public/${eventData.publicLinkId}`);
            }
         } else {
            setError('Event not found');
         }
      } catch (err) {
         setError('Failed to load event details');
      } finally {
         setLoading(false);
      }
   };

   const fetchQRCodeStats = async () => {
      if (!eventId) return;

      setLoadingStats(true);
      try {
         const stats = await qrCodeService.getStats(eventId);
         setQRCodeStats(stats);
      } catch (error) {
         console.error('Error fetching QR code stats:', error);
      } finally {
         setLoadingStats(false);
      }
   };

   const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
      setTabValue(newValue);
   };

   const handleEditEvent = () => {
      if (!id || !eventId) return;

      navigate(`/organizations/${id}/events/${eventId}/edit`);
   };

   const handleDeleteEvent = async () => {
      if (deleteConfirmText !== event?.title) {
         return;
      }

      try {
         setLoading(true);

         // TODO: Replace with actual API call to delete event
         await new Promise((resolve) => setTimeout(resolve, 1000));

         setLoading(false);
         setDeleteDialogOpen(false);
         navigate(`/organizations/${id}/events`);
      } catch (err) {
         console.error('Error deleting event:', err);
         setError('Failed to delete event. Please try again.');
         setLoading(false);
      }
   };

   const handleShareDialogOpen = () => {
      setShareDialogOpen(true);
   };

   const handleShareDialogClose = () => {
      setShareDialogOpen(false);
   };

   const handleDeleteDialogOpen = () => {
      setDeleteDialogOpen(true);
   };

   const handleDeleteDialogClose = () => {
      setDeleteDialogOpen(false);
      setDeleteConfirmText('');
   };

   const handleCopyEventLink = () => {
      const url = window.location.href;
      navigator.clipboard.writeText(url);
      // You could add a toast notification here
   };

   const handleCheckIn = async (attendeeId: string, isCheckedIn: boolean) => {
      try {
         setLoading(true);

         // Get the event document reference
         const eventRef = doc(db, 'events', eventId!);

         // Get current event data
         const eventSnapshot = await getDoc(eventRef);
         if (!eventSnapshot.exists()) {
            throw new Error('Event not found');
         }

         const eventData = eventSnapshot.data();
         const attendees = eventData.attendees || [];

         // Find and update the attendee's check-in status
         const updatedAttendees = attendees.map((attendee: any) => {
            if (attendee.id === attendeeId) {
               return {
                  ...attendee,
                  checkInTime: isCheckedIn ? null : new Date(),
               };
            }
            return attendee;
         });

         // Update the event document
         await updateDoc(eventRef, {
            attendees: updatedAttendees,
            lastUpdated: serverTimestamp(),
         });

         // Refresh event details
         await fetchEventDetails();
      } catch (err) {
         console.error('Error updating check-in status:', err);
         setError('Failed to update check-in status');
      } finally {
         setLoading(false);
      }
   };

   const formatDateRange = (start: Date | string, end: Date | string) => {
      // Convert to Date objects if they're strings
      const startDate = start instanceof Date ? start : new Date(start);
      const endDate = end instanceof Date ? end : new Date(end);

      const sameDay = startDate.toDateString() === endDate.toDateString();

      if (sameDay) {
         return `${startDate.toLocaleDateString()} from ${startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} to ${endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      } else {
         return `${startDate.toLocaleDateString()} ${startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${endDate.toLocaleDateString()} ${endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      }
   };

   const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat('en-US', {
         style: 'currency',
         currency: 'USD',
      }).format(amount);
   };

   const generatePublicLink = async () => {
      if (!event || !eventId) return;

      try {
         setGeneratingLink(true);

         // Create a unique ID for the public link
         const publicLinkId = `${eventId}-${Date.now()}`;

         // Update the event with the public link ID
         const eventRef = doc(db, 'events', eventId);
         await updateDoc(eventRef, {
            publicLinkId,
            isPublic: true,
            lastUpdated: serverTimestamp(),
         });

         // Create the full public link
         const baseUrl = window.location.origin;
         const link = `${baseUrl}/events/public/${publicLinkId}`;
         setPublicLink(link);

         // Open the share dialog
         setShareDialogOpen(true);
      } catch (err) {
         console.error('Error generating public link:', err);
         setError('Failed to generate public link. Please try again.');
      } finally {
         setGeneratingLink(false);
      }
   };

   const getShareUrl = () => {
      return `${window.location.origin}/events/${event?.id}`;
   };

   const handleQRCodeClick = () => {
      setQrCodeModalOpen(true);
   };

   const calculateBreakEven = () => {
      if (!event || totalExpenses === 0) return null;

      const averageTicketPrice = event.ticketTypes?.reduce((sum, type) => sum + type.price, 0) || 0;
      if (averageTicketPrice === 0) return null;

      return Math.ceil(totalExpenses / averageTicketPrice);
   };

   const calculateProfit = () => {
      if (!event) return 0;
      return (event.revenue || 0) - totalExpenses;
   };

   const handleAddExpense = async () => {
      if (!event || !newExpense.description || !newExpense.amount) return;

      try {
         const expenseData = {
            id: Date.now().toString(),
            description: newExpense.description,
            amount: parseFloat(newExpense.amount),
            date: new Date(),
         };

         const updatedExpenses = [...expenses, expenseData];

         // Update Firestore
         const eventRef = doc(db, 'events', eventId!);
         await updateDoc(eventRef, {
            expenses: updatedExpenses,
            totalExpenses: totalExpenses + expenseData.amount,
         });

         setExpenses(updatedExpenses);
         setNewExpense({ description: '', amount: '' });
      } catch (err) {
         console.error('Error adding expense:', err);
         setError('Failed to add expense. Please try again.');
      }
   };

   const handleDeleteExpense = async (expenseId: string) => {
      try {
         const updatedExpenses = expenses.filter((exp) => exp.id !== expenseId);

         // Update Firestore
         const eventRef = doc(db, 'events', eventId!);
         await updateDoc(eventRef, {
            expenses: updatedExpenses,
            totalExpenses: updatedExpenses.reduce((sum, exp) => sum + exp.amount, 0),
         });

         setExpenses(updatedExpenses);
      } catch (err) {
         console.error('Error deleting expense:', err);
         setError('Failed to delete expense. Please try again.');
      }
   };

   const handlePreviewOpen = () => {
      setPreviewOpen(true);
   };

   const handlePreviewClose = () => {
      setPreviewOpen(false);
   };

   const PublicEventPreview = () => {
      if (!event) return null;

      const formatTime = (date: any) => {
         if (!date) return 'TBD';
         try {
            const d = date instanceof Date ? date : new Date(date);
            return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
         } catch (err) {
            return 'Invalid time';
         }
      };

      const hasAvailableTickets =
         event.ticketTypes?.some((ticket) => ticket.capacity - (ticket.ticketsSold || 0) > 0) ??
         false;

      return (
         <Box sx={{ maxHeight: '80vh', overflow: 'auto' }}>
            {/* Event Cover Image */}
            <Box sx={{ position: 'relative' }}>
               {event.coverPhotoUrl ? (
                  <Box
                     component='img'
                     src={event.coverPhotoUrl}
                     alt={event.title}
                     sx={{
                        width: '100%',
                        height: 300,
                        objectFit: 'cover',
                     }}
                  />
               ) : (
                  <Box
                     sx={{
                        height: 200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'primary.light',
                     }}
                  >
                     <EventIcon sx={{ fontSize: 80, color: 'white', opacity: 0.7 }} />
                  </Box>
               )}
            </Box>

            <Box sx={{ p: 3 }}>
               {/* Event Header */}
               <Box sx={{ mb: 3 }}>
                  {activeOrganization && (
                     <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                        Presented by {activeOrganization.name}
                     </Typography>
                  )}
                  <Typography variant='h4' component='h1' gutterBottom>
                     {event.title}
                  </Typography>
                  <Chip label={event.eventType} color='primary' size='small' sx={{ mr: 1 }} />
               </Box>

               {/* Event Details */}
               <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6}>
                     <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <DateRangeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography>{formatDateRange(event.startDate, event.endDate)}</Typography>
                     </Box>
                     <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography>
                           {formatTime(event.startDate)} - {formatTime(event.endDate)}
                        </Typography>
                     </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                     <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography>
                           {typeof event.location === 'string'
                              ? event.location
                              : event.location?.name || event.location?.address || 'Location TBD'}
                        </Typography>
                     </Box>
                  </Grid>
               </Grid>

               {/* Ticket Types */}
               {event.ticketTypes && event.ticketTypes.length > 0 && (
                  <>
                     <Typography variant='h6' gutterBottom>
                        Available Tickets
                     </Typography>
                     <Grid container spacing={2} sx={{ mb: 4 }}>
                        {event.ticketTypes.map((ticket) => (
                           <Grid item xs={12} sm={6} key={ticket.id}>
                              <Card variant='outlined'>
                                 <CardContent>
                                    <Typography variant='h6' gutterBottom>
                                       {ticket.name}
                                    </Typography>
                                    {ticket.description && (
                                       <Typography variant='body2' color='text.secondary' paragraph>
                                          {ticket.description}
                                       </Typography>
                                    )}
                                    <Typography variant='h5' color='primary' gutterBottom>
                                       {ticket.price > 0 ? formatCurrency(ticket.price) : 'Free'}
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                       {ticket.capacity - (ticket.ticketsSold || 0)} tickets
                                       remaining
                                    </Typography>
                                 </CardContent>
                              </Card>
                           </Grid>
                        ))}
                     </Grid>
                  </>
               )}

               {/* Event Description */}
               <Typography variant='h6' gutterBottom>
                  About This Event
               </Typography>
               <Typography variant='body1' sx={{ whiteSpace: 'pre-line', mb: 4 }}>
                  {event.description}
               </Typography>

               {/* Registration Status */}
               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                     <Typography variant='body2' color='text.secondary'>
                        {event.ticketsSold || 0}{' '}
                        {(event.ticketsSold || 0) === 1 ? 'person' : 'people'} registered
                     </Typography>
                  </Box>
                  <Button variant='contained' size='large' disabled={!hasAvailableTickets}>
                     {hasAvailableTickets ? 'Register Now' : 'Sold Out'}
                  </Button>
               </Box>
            </Box>
         </Box>
      );
   };

   const handleMarketingSubTabChange = (event: React.SyntheticEvent, newValue: number) => {
      setMarketingSubTabValue(newValue);
   };

   const handleCreateBanners = () => {
      setMarketingSubTabValue(2);
      setCurrentAssetType('banner');
      setBannerDialogOpen(true);
   };

   const handleCreateGraphics = () => {
      setMarketingSubTabValue(2);
      setCurrentAssetType('social');
      setBannerDialogOpen(true);
   };

   const handleCreateMaterials = () => {
      setMarketingSubTabValue(2);
      setCurrentAssetType('flyer');
      setBannerDialogOpen(true);
   };

   const generateImageWithStabilityAI = async () => {
      if (!event) return;

      try {
         setGeneratingImage(true);

         // Format dates for prompt
         const formattedStartDate =
            event.startDate instanceof Date
               ? event.startDate.toLocaleDateString()
               : new Date(event.startDate).toLocaleDateString();

         const formattedStartTime =
            event.startDate instanceof Date
               ? event.startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
               : new Date(event.startDate).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                 });

         // Build location string
         const locationString =
            typeof event.location === 'string'
               ? event.location
               : event.location?.name || event.location?.address || 'TBD';

         // Create base request object
         const request = {
            prompt: imagePrompt,
            eventTitle: event.title,
            eventDescription: event.description || '',
            eventType: event.eventType || 'Event',
            eventDate: event.startDate,
            eventLocation: locationString,
            assetType: currentAssetType,
            style: imageStyle,
         };

         let result;

         // Call the appropriate service method based on asset type
         switch (currentAssetType) {
            case 'banner':
               result = await visualAssetService.generateEventBanner(request);
               break;
            case 'social':
               result = await visualAssetService.generateSocialGraphic(request);
               break;
            case 'flyer':
               result = await visualAssetService.generatePromotionalMaterial(request);
               break;
            default:
               result = await visualAssetService.generateEventBanner(request);
         }

         // Set the generated image URL
         setGeneratedImageUrl(result.imageUrl);

         // If no user prompt was provided, set it to the one that was used
         if (!imagePrompt) {
            setImagePrompt(result.prompt);
         }
      } catch (error) {
         console.error('Error generating image with Stability AI:', error);
         // You would add error handling here
      } finally {
         setGeneratingImage(false);
      }
   };

   const saveGeneratedAsset = async () => {
      if (!event || !generatedImageUrl || !id || !eventId) return;

      try {
         const assetTitle =
            selectedBanner?.title ||
            `${event.title} ${
               currentAssetType === 'banner'
                  ? 'Banner'
                  : currentAssetType === 'social'
                    ? 'Social Media Graphic'
                    : 'Flyer'
            }`;

         const assetDescription = selectedBanner?.description || bannerDescription || imagePrompt;

         // Save the image to Firebase storage
         const downloadUrl = await visualAssetService.saveAssetToStorage(
            generatedImageUrl,
            id,
            eventId,
            {
               title: assetTitle,
               description: assetDescription,
               type: currentAssetType,
               style: imageStyle,
               prompt: imagePrompt,
               createdAt: new Date().toISOString(),
            }
         );

         // Also save complete metadata to Firestore
         try {
            const assetId = `${currentAssetType}-${nanoid(16)}`;
            const assetsCollection = collection(db, 'events', eventId, 'assets');

            await addDoc(assetsCollection, {
               id: assetId,
               imageUrl: downloadUrl,
               title: assetTitle,
               description: assetDescription,
               type: currentAssetType,
               style: imageStyle,
               prompt: imagePrompt,
               dimensions:
                  currentAssetType === 'banner'
                     ? { width: 1200, height: 300 }
                     : currentAssetType === 'social'
                       ? { width: 1080, height: 1080 }
                       : { width: 800, height: 1100 },
               createdAt: new Date().toISOString(),
               eventId: eventId,
               organizationId: id,
            });
         } catch (firestoreErr) {
            console.error('Error saving asset metadata to Firestore:', firestoreErr);
            // Continue even if Firestore save fails, since we have the image URL
         }

         // Close the dialog
         setBannerDialogOpen(false);

         // Reset states
         setGeneratedImageUrl(null);
         setImagePrompt('');
         setBannerDescription('');
         setSelectedBanner(null);

         // Refresh the assets list (you would need to implement this)
         // Refreshing will happen automatically when user switches to Assets tab
      } catch (error) {
         console.error('Error saving asset:', error);
         // Handle error
      }
   };

   const marketingTabContent = (
      <Box>
         <Tabs
            value={marketingSubTabValue}
            onChange={handleMarketingSubTabChange}
            aria-label='marketing tabs'
            variant='fullWidth'
            sx={{ mb: 3 }}
         >
            <Tab label='Social Media Posts' icon={<ChatIcon />} {...a11yProps(0)} />
            <Tab label='Campaigns' icon={<CampaignIcon />} {...a11yProps(1)} />
            <Tab label='Visual Assets' icon={<ImageIcon />} {...a11yProps(2)} />
            <Tab label='Assets Gallery' icon={<GridViewIcon />} {...a11yProps(3)} />
         </Tabs>

         <Box sx={{ display: marketingSubTabValue === 0 ? 'block' : 'none' }}>
            <OrganizationMarketingTab
               organizationId={id || ''}
               eventId={eventId || ''}
               eventTitle={event?.title || ''}
               eventDescription={event?.description || ''}
               eventType={event?.eventType || ''}
               eventDate={event?.startDate || new Date()}
               eventLocation={
                  typeof event?.location === 'string'
                     ? event?.location
                     : event?.location?.name || ''
               }
            />
         </Box>

         <Box sx={{ display: marketingSubTabValue === 1 ? 'block' : 'none' }}>
            <CampaignsTable
               organizationId={id || ''}
               eventId={eventId || ''}
               eventDetails={{
                  title: event?.title || '',
                  description: event?.description || '',
                  type: event?.eventType || '',
                  date: event?.startDate || new Date(),
                  location:
                     typeof event?.location === 'string'
                        ? event?.location
                        : event?.location?.name || '',
               }}
            />
         </Box>

         <Box sx={{ display: marketingSubTabValue === 2 ? 'block' : 'none' }}>
            <Paper sx={{ p: 3, mb: 3 }}>
               <Box
                  sx={{
                     display: 'flex',
                     justifyContent: 'space-between',
                     alignItems: 'center',
                     mb: 3,
                  }}
               >
                  <Typography variant='h6'>Visual Assets</Typography>
                  <Box sx={{ mt: 3 }}>
                     <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                           <Button
                              fullWidth
                              onClick={() => {
                                 setBannerDialogOpen(true);
                                 setCurrentAssetType('banner');
                              }}
                           >
                              Create Banners
                           </Button>
                        </Grid>
                        <Grid item xs={12} md={4}>
                           <Button
                              fullWidth
                              onClick={() => {
                                 setBannerDialogOpen(true);
                                 setCurrentAssetType('social');
                              }}
                           >
                              Create Graphics
                           </Button>
                        </Grid>
                        <Grid item xs={12} md={4}>
                           <Button
                              fullWidth
                              onClick={() => {
                                 setBannerDialogOpen(true);
                                 setCurrentAssetType('flyer');
                              }}
                           >
                              Create Materials
                           </Button>
                        </Grid>
                     </Grid>
                  </Box>
               </Box>

               <Alert severity='info' sx={{ mb: 3 }}>
                  Create visual assets for social media, event promotions, and marketing materials.
               </Alert>

               <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                     <Card>
                        <CardContent>
                           <Typography variant='h6' gutterBottom>
                              Event Banners
                           </Typography>
                           <Typography variant='body2' color='text.secondary'>
                              Create banners optimized for different platforms
                           </Typography>
                        </CardContent>
                        <Box sx={{ p: 2, pt: 0 }}>
                           <Button fullWidth variant='outlined' onClick={handleCreateBanners}>
                              Create Banners
                           </Button>
                        </Box>
                     </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                     <Card>
                        <CardContent>
                           <Typography variant='h6' gutterBottom>
                              Social Graphics
                           </Typography>
                           <Typography variant='body2' color='text.secondary'>
                              Generate social media graphics with your event details
                           </Typography>
                        </CardContent>
                        <Box sx={{ p: 2, pt: 0 }}>
                           <Button fullWidth variant='outlined' onClick={handleCreateGraphics}>
                              Create Graphics
                           </Button>
                        </Box>
                     </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                     <Card>
                        <CardContent>
                           <Typography variant='h6' gutterBottom>
                              Promotional Materials
                           </Typography>
                           <Typography variant='body2' color='text.secondary'>
                              Create flyers and posters for your event
                           </Typography>
                        </CardContent>
                        <Box sx={{ p: 2, pt: 0 }}>
                           <Button fullWidth variant='outlined' onClick={handleCreateMaterials}>
                              Create Materials
                           </Button>
                        </Box>
                     </Card>
                  </Grid>
               </Grid>
            </Paper>
         </Box>

         <Box sx={{ display: marketingSubTabValue === 3 ? 'block' : 'none' }}>
            <EventAssetsTable
               organizationId={id || ''}
               eventId={eventId || ''}
               eventTitle={event?.title}
            />
         </Box>
      </Box>
   );

   if (loading) {
      return (
         <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
         </Box>
      );
   }

   if (error || !event) {
      return (
         <Container maxWidth='lg' sx={{ mt: 4 }}>
            <Alert severity={error ? 'error' : 'warning'}>{error || 'Event not found'}</Alert>
         </Container>
      );
   }

   // Make sure attendees is defined before using filter on it
   const attendees = event.attendees || [];
   const ticketPercentage = Math.round((event.ticketsSold / event.maxCapacity) * 100);
   const checkedInCount = attendees.filter((a) => a.checkInTime).length;
   const checkedInPercentage =
      attendees.length > 0 ? Math.round((checkedInCount / attendees.length) * 100) : 0;

   // Ensure dates are proper Date objects
   const startDate = event.startDate instanceof Date ? event.startDate : new Date(event.startDate);
   const endDate = event.endDate instanceof Date ? event.endDate : new Date(event.endDate);

   const safeEvent = {
      ...event,
      startDate,
      endDate,
      attendees,
   };

   // Define tabs array with proper typing
   const tabs: TabDefinition[] = [
      {
         label: 'Overview',
         icon: <InfoIcon />,
         content: (
            <Box sx={{ p: 3 }}>
               <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                     <Typography variant='h6' gutterBottom>
                        About this Event
                     </Typography>
                     <Typography variant='body1' paragraph>
                        {event.description}
                     </Typography>

                     <Divider sx={{ my: 3 }} />

                     <Typography variant='h6' gutterBottom>
                        Ticket Types
                     </Typography>
                     {event.ticketTypes && event.ticketTypes.length > 0 ? (
                        <Grid container spacing={2}>
                           {event.ticketTypes.map((ticket) => (
                              <Grid item xs={12} md={6} key={ticket.id}>
                                 <Paper sx={{ p: 2 }}>
                                    <Box
                                       sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          mb: 1,
                                       }}
                                    >
                                       <Typography variant='subtitle1'>{ticket.name}</Typography>
                                       <Typography variant='subtitle1' color='primary'>
                                          ${ticket.price}
                                       </Typography>
                                    </Box>
                                    <Typography variant='body2' color='text.secondary' paragraph>
                                       {ticket.description}
                                    </Typography>
                                    <Box
                                       sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                       }}
                                    >
                                       <Typography variant='body2'>
                                          {ticket.ticketsSold}/{ticket.capacity} sold
                                       </Typography>
                                       <Chip
                                          icon={
                                             ticket.isVisible ? <VisibilityIcon /> : <HiddenIcon />
                                          }
                                          label={ticket.isVisible ? 'Visible' : 'Hidden'}
                                          size='small'
                                          color={ticket.isVisible ? 'success' : 'default'}
                                          variant='outlined'
                                       />
                                    </Box>
                                 </Paper>
                              </Grid>
                           ))}
                        </Grid>
                     ) : (
                        <Typography variant='body2' color='text.secondary'>
                           No ticket types defined
                        </Typography>
                     )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                     <Card variant='outlined'>
                        <CardContent>
                           <Typography variant='h6' gutterBottom>
                              Quick Actions
                           </Typography>
                           <Stack spacing={2}>
                              <Button
                                 fullWidth
                                 variant='contained'
                                 startIcon={<QrCodeIcon />}
                                 onClick={handleQRCodeClick}
                              >
                                 View QR Code
                              </Button>
                              <Button
                                 fullWidth
                                 variant='outlined'
                                 startIcon={<ShareIcon />}
                                 onClick={handleShareDialogOpen}
                              >
                                 Share Event
                              </Button>
                              <Button
                                 fullWidth
                                 variant='outlined'
                                 startIcon={<VisibilityIcon />}
                                 onClick={handlePreviewOpen}
                              >
                                 Preview Public View
                              </Button>
                              <Button
                                 fullWidth
                                 variant='outlined'
                                 startIcon={<EditIcon />}
                                 onClick={handleEditEvent}
                              >
                                 Edit Event
                              </Button>
                              <Button
                                 fullWidth
                                 variant='outlined'
                                 startIcon={<CodeIcon />}
                                 onClick={() => setEmbedCodeModalOpen(true)}
                              >
                                 View Embed Code
                              </Button>
                           </Stack>
                        </CardContent>
                     </Card>
                  </Grid>
               </Grid>
            </Box>
         ),
      },
      {
         label: 'Attendees',
         icon: <PeopleIcon />,
         content: (
            <Box sx={{ p: 3 }}>
               <Box
                  sx={{
                     display: 'flex',
                     justifyContent: 'space-between',
                     alignItems: 'center',
                     mb: 3,
                  }}
               >
                  <Typography variant='h6'>Event Attendees</Typography>
                  <TestDataGenerator eventId={eventId!} onDataGenerated={fetchEventDetails} />
               </Box>

               <TableContainer>
                  <Table>
                     <TableHead>
                        <TableRow>
                           <TableCell>Name</TableCell>
                           <TableCell>Email</TableCell>
                           <TableCell>Ticket Type</TableCell>
                           <TableCell>Purchase Date</TableCell>
                           <TableCell>Check-in Time</TableCell>
                        </TableRow>
                     </TableHead>
                     <TableBody>
                        {event.attendees?.map((attendee) => (
                           <TableRow key={attendee.id}>
                              <TableCell>{attendee.name}</TableCell>
                              <TableCell>{attendee.email}</TableCell>
                              <TableCell>
                                 {event.ticketTypes?.find((t) => t.id === attendee.ticketType)
                                    ?.name || attendee.ticketType}
                              </TableCell>
                              <TableCell>
                                 {attendee.purchaseDate instanceof Date
                                    ? attendee.purchaseDate.toLocaleDateString()
                                    : new Date(attendee.purchaseDate).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                 {attendee.checkInTime
                                    ? attendee.checkInTime instanceof Date
                                       ? attendee.checkInTime.toLocaleString()
                                       : new Date(attendee.checkInTime).toLocaleString()
                                    : 'Not checked in'}
                              </TableCell>
                           </TableRow>
                        ))}
                        {(!event.attendees || event.attendees.length === 0) && (
                           <TableRow>
                              <TableCell colSpan={5} align='center'>
                                 No attendees yet
                              </TableCell>
                           </TableRow>
                        )}
                     </TableBody>
                  </Table>
               </TableContainer>
            </Box>
         ),
      },
      {
         label: 'Check-in',
         icon: <CheckInIcon />,
         content: (
            <Box sx={{ p: 3 }}>
               <Box
                  sx={{
                     display: 'flex',
                     justifyContent: 'space-between',
                     alignItems: 'center',
                     mb: 3,
                  }}
               >
                  <Typography variant='h6'>Check-in Management</Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                     <Button variant='contained' startIcon={<QrCodeIcon />}>
                        QR Code Scanner
                     </Button>
                     <TestDataGenerator eventId={eventId!} onDataGenerated={fetchEventDetails} />
                  </Box>
               </Box>

               <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={4}>
                     <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                           <Typography variant='h4' gutterBottom>
                              {checkedInCount}/{event.ticketsSold}
                           </Typography>
                           <Typography variant='subtitle1' color='text.secondary'>
                              Checked In
                           </Typography>
                           <LinearProgress
                              variant='determinate'
                              value={checkedInPercentage}
                              sx={{ mt: 2, height: 8, borderRadius: 4 }}
                           />
                        </CardContent>
                     </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                     <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                           <Typography variant='h4' gutterBottom>
                              {event.ticketsSold - checkedInCount}
                           </Typography>
                           <Typography variant='subtitle1' color='text.secondary'>
                              Not Checked In
                           </Typography>
                        </CardContent>
                     </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                     <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                           <Typography variant='h4' gutterBottom>
                              {checkedInPercentage}%
                           </Typography>
                           <Typography variant='subtitle1' color='text.secondary'>
                              Check-in Rate
                           </Typography>
                        </CardContent>
                     </Card>
                  </Grid>
               </Grid>

               <TableContainer component={Paper}>
                  <Table>
                     <TableHead>
                        <TableRow>
                           <TableCell>Name</TableCell>
                           <TableCell>Email</TableCell>
                           <TableCell>Ticket Type</TableCell>
                           <TableCell>Purchase Date</TableCell>
                           <TableCell>Check-in Status</TableCell>
                           <TableCell>Actions</TableCell>
                        </TableRow>
                     </TableHead>
                     <TableBody>
                        {event.attendees?.map((attendee) => (
                           <TableRow key={attendee.id}>
                              <TableCell>{attendee.name}</TableCell>
                              <TableCell>{attendee.email}</TableCell>
                              <TableCell>
                                 {event.ticketTypes?.find((t) => t.id === attendee.ticketType)
                                    ?.name || attendee.ticketType}
                              </TableCell>
                              <TableCell>
                                 {attendee.purchaseDate instanceof Date
                                    ? attendee.purchaseDate.toLocaleDateString()
                                    : new Date(attendee.purchaseDate).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                 {attendee.checkInTime ? (
                                    <Chip
                                       label={`Checked in at ${
                                          attendee.checkInTime instanceof Date
                                             ? attendee.checkInTime.toLocaleTimeString()
                                             : new Date(attendee.checkInTime).toLocaleTimeString()
                                       }`}
                                       color='success'
                                       variant='outlined'
                                    />
                                 ) : (
                                    <Chip
                                       label='Not checked in'
                                       color='default'
                                       variant='outlined'
                                    />
                                 )}
                              </TableCell>
                              <TableCell>
                                 <Button
                                    variant='contained'
                                    size='small'
                                    color={attendee.checkInTime ? 'error' : 'primary'}
                                    onClick={() =>
                                       handleCheckIn(attendee.id, !!attendee.checkInTime)
                                    }
                                 >
                                    {attendee.checkInTime ? 'Undo Check-in' : 'Check In'}
                                 </Button>
                              </TableCell>
                           </TableRow>
                        ))}
                        {(!event.attendees || event.attendees.length === 0) && (
                           <TableRow>
                              <TableCell colSpan={6} align='center'>
                                 No attendees yet
                              </TableCell>
                           </TableRow>
                        )}
                     </TableBody>
                  </Table>
               </TableContainer>
            </Box>
         ),
      },
      {
         label: 'Finances',
         icon: <MoneyIcon />,
         content: (
            <Box sx={{ p: 3 }}>
               <Typography variant='h6' gutterBottom>
                  Financial Overview
               </Typography>

               {/* Financial Summary Cards */}
               <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={3}>
                     <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                           <MonetizationIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                           <Typography variant='h4' gutterBottom>
                              ${event.revenue || 0}
                           </Typography>
                           <Typography variant='subtitle1' color='text.secondary'>
                              Total Revenue
                           </Typography>
                        </CardContent>
                     </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                     <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                           <TicketIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                           <Typography variant='h4' gutterBottom>
                              {event.ticketsSold}
                           </Typography>
                           <Typography variant='subtitle1' color='text.secondary'>
                              Tickets Sold
                           </Typography>
                        </CardContent>
                     </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                     <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                           <MoneyIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                           <Typography variant='h4' gutterBottom>
                              $
                              {event.ticketsSold > 0
                                 ? (event.revenue / event.ticketsSold).toFixed(2)
                                 : '0.00'}
                           </Typography>
                           <Typography variant='subtitle1' color='text.secondary'>
                              Average Ticket Price
                           </Typography>
                        </CardContent>
                     </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                     <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                           <GroupIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                           <Typography variant='h4' gutterBottom>
                              {((event.ticketsSold / event.maxCapacity) * 100).toFixed(1)}%
                           </Typography>
                           <Typography variant='subtitle1' color='text.secondary'>
                              Capacity Sold
                           </Typography>
                        </CardContent>
                     </Card>
                  </Grid>
               </Grid>

               {/* Revenue by Ticket Type */}
               <Typography variant='h6' gutterBottom sx={{ mt: 4 }}>
                  Revenue by Ticket Type
               </Typography>
               <TableContainer component={Paper} sx={{ mb: 4 }}>
                  <Table>
                     <TableHead>
                        <TableRow>
                           <TableCell>Ticket Type</TableCell>
                           <TableCell align='right'>Price</TableCell>
                           <TableCell align='right'>Sold</TableCell>
                           <TableCell align='right'>Available</TableCell>
                           <TableCell align='right'>Revenue</TableCell>
                           <TableCell align='right'>% of Total</TableCell>
                        </TableRow>
                     </TableHead>
                     <TableBody>
                        {event.ticketTypes?.map((ticket) => {
                           const ticketRevenue = ticket.price * ticket.ticketsSold;
                           const percentOfTotal = event.revenue
                              ? ((ticketRevenue / event.revenue) * 100).toFixed(1)
                              : '0';
                           return (
                              <TableRow key={ticket.id}>
                                 <TableCell>{ticket.name}</TableCell>
                                 <TableCell align='right'>${ticket.price}</TableCell>
                                 <TableCell align='right'>{ticket.ticketsSold}</TableCell>
                                 <TableCell align='right'>
                                    {ticket.capacity - ticket.ticketsSold}
                                 </TableCell>
                                 <TableCell align='right'>${ticketRevenue}</TableCell>
                                 <TableCell align='right'>{percentOfTotal}%</TableCell>
                              </TableRow>
                           );
                        })}
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                           <TableCell>
                              <strong>Total</strong>
                           </TableCell>
                           <TableCell align='right'>-</TableCell>
                           <TableCell align='right'>
                              <strong>{event.ticketsSold}</strong>
                           </TableCell>
                           <TableCell align='right'>
                              <strong>{event.maxCapacity - event.ticketsSold}</strong>
                           </TableCell>
                           <TableCell align='right'>
                              <strong>${event.revenue || 0}</strong>
                           </TableCell>
                           <TableCell align='right'>
                              <strong>100%</strong>
                           </TableCell>
                        </TableRow>
                     </TableBody>
                  </Table>
               </TableContainer>

               {/* Sales Timeline */}
               <Typography variant='h6' gutterBottom sx={{ mt: 4 }}>
                  Sales Timeline
               </Typography>
               <TableContainer component={Paper}>
                  <Table>
                     <TableHead>
                        <TableRow>
                           <TableCell>Purchase Date</TableCell>
                           <TableCell>Attendee</TableCell>
                           <TableCell>Ticket Type</TableCell>
                           <TableCell align='right'>Amount</TableCell>
                        </TableRow>
                     </TableHead>
                     <TableBody>
                        {event.attendees
                           ?.sort((a, b) => {
                              const dateA = new Date(a.purchaseDate);
                              const dateB = new Date(b.purchaseDate);
                              return dateB.getTime() - dateA.getTime();
                           })
                           .map((attendee) => {
                              const ticketType = event.ticketTypes?.find(
                                 (t) => t.id === attendee.ticketType
                              );
                              return (
                                 <TableRow key={attendee.id}>
                                    <TableCell>
                                       {attendee.purchaseDate instanceof Date
                                          ? attendee.purchaseDate.toLocaleDateString()
                                          : new Date(attendee.purchaseDate).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell>{attendee.name}</TableCell>
                                    <TableCell>{ticketType?.name || attendee.ticketType}</TableCell>
                                    <TableCell align='right'>${ticketType?.price || 0}</TableCell>
                                 </TableRow>
                              );
                           })}
                        {(!event.attendees || event.attendees.length === 0) && (
                           <TableRow>
                              <TableCell colSpan={4} align='center'>
                                 No sales recorded yet
                              </TableCell>
                           </TableRow>
                        )}
                     </TableBody>
                  </Table>
               </TableContainer>

               {/* Financial Metrics */}
               <Typography variant='h6' gutterBottom sx={{ mt: 4 }}>
                  Financial Metrics
               </Typography>
               <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                     <Card>
                        <CardContent>
                           <Typography variant='subtitle1' gutterBottom>
                              Projected Revenue at Full Capacity
                           </Typography>
                           <Typography variant='h5'>
                              $
                              {event.ticketTypes && event.ticketTypes.length > 0
                                 ? event.ticketTypes.reduce(
                                      (total, ticket) => total + ticket.price * ticket.capacity,
                                      0
                                   )
                                 : event.isPaid
                                   ? event.price * event.maxCapacity
                                   : 0}
                           </Typography>
                           <LinearProgress
                              variant='determinate'
                              value={
                                 event.ticketTypes && event.ticketTypes.length > 0
                                    ? (event.revenue /
                                         Math.max(
                                            1,
                                            event.ticketTypes.reduce(
                                               (total, ticket) =>
                                                  total + ticket.price * ticket.capacity,
                                               0
                                            )
                                         )) *
                                      100
                                    : event.isPaid
                                      ? (event.revenue /
                                           Math.max(1, event.price * event.maxCapacity)) *
                                        100
                                      : 0
                              }
                              sx={{ mt: 2 }}
                           />
                        </CardContent>
                     </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                     <Card>
                        <CardContent>
                           <Typography variant='subtitle1' gutterBottom>
                              Revenue per Available Ticket
                           </Typography>
                           <Typography variant='h5'>
                              $
                              {event.maxCapacity
                                 ? (event.revenue / event.maxCapacity).toFixed(2)
                                 : '0.00'}
                           </Typography>
                        </CardContent>
                     </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                     <Card>
                        <CardContent>
                           <Typography variant='subtitle1' gutterBottom>
                              Average Daily Sales
                           </Typography>
                           <Typography variant='h5'>
                              $
                              {event.ticketsSold > 0
                                 ? (
                                      event.revenue /
                                      Math.max(
                                         1,
                                         Math.ceil(
                                            (new Date().getTime() -
                                               new Date(event.startDate).getTime()) /
                                               (1000 * 60 * 60 * 24)
                                         )
                                      )
                                   ).toFixed(2)
                                 : '0.00'}
                           </Typography>
                        </CardContent>
                     </Card>
                  </Grid>
               </Grid>
            </Box>
         ),
      },
      {
         label: 'Expenses',
         icon: <ReceiptIcon />,
         content: (
            <Grid container spacing={3}>
               {/* Expense Summary */}
               <Grid item xs={12}>
                  <Paper sx={{ p: 3, mb: 3 }}>
                     <Typography
                        variant='h6'
                        gutterBottom
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                     >
                        <CalculateIcon />
                        Financial Overview
                     </Typography>
                     <Grid container spacing={3}>
                        <Grid item xs={12} md={3}>
                           <Box sx={{ textAlign: 'center', p: 2 }}>
                              <Typography variant='subtitle2' color='text.secondary'>
                                 Total Revenue
                              </Typography>
                              <Typography variant='h4'>
                                 {formatCurrency(event?.revenue || 0)}
                              </Typography>
                           </Box>
                        </Grid>
                        <Grid item xs={12} md={3}>
                           <Box sx={{ textAlign: 'center', p: 2 }}>
                              <Typography variant='subtitle2' color='text.secondary'>
                                 Total Expenses
                              </Typography>
                              <Typography variant='h4'>{formatCurrency(totalExpenses)}</Typography>
                           </Box>
                        </Grid>
                        <Grid item xs={12} md={3}>
                           <Box sx={{ textAlign: 'center', p: 2 }}>
                              <Typography variant='subtitle2' color='text.secondary'>
                                 Net Profit
                              </Typography>
                              <Typography
                                 variant='h4'
                                 color={calculateProfit() >= 0 ? 'success.main' : 'error.main'}
                              >
                                 {formatCurrency(calculateProfit())}
                              </Typography>
                           </Box>
                        </Grid>
                        <Grid item xs={12} md={3}>
                           <Box sx={{ textAlign: 'center', p: 2 }}>
                              <Typography variant='subtitle2' color='text.secondary'>
                                 Break-even Point
                              </Typography>
                              <Typography variant='h4'>
                                 {calculateBreakEven() ? `${calculateBreakEven()} tickets` : 'N/A'}
                              </Typography>
                           </Box>
                        </Grid>
                     </Grid>
                  </Paper>
               </Grid>

               {/* Add New Expense */}
               <Grid item xs={12}>
                  <Paper sx={{ p: 3 }}>
                     <Typography variant='h6' gutterBottom>
                        Add Expense
                     </Typography>
                     <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                           <TextField
                              fullWidth
                              label='Description'
                              value={newExpense.description}
                              onChange={(e) =>
                                 setNewExpense((prev) => ({ ...prev, description: e.target.value }))
                              }
                           />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                           <TextField
                              fullWidth
                              label='Amount'
                              type='number'
                              value={newExpense.amount}
                              onChange={(e) =>
                                 setNewExpense((prev) => ({ ...prev, amount: e.target.value }))
                              }
                              InputProps={{
                                 startAdornment: (
                                    <InputAdornment position='start'>$</InputAdornment>
                                 ),
                              }}
                           />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                           <Button
                              fullWidth
                              variant='contained'
                              onClick={handleAddExpense}
                              disabled={!newExpense.description || !newExpense.amount}
                              startIcon={<AddIcon />}
                              sx={{ height: '100%' }}
                           >
                              Add
                           </Button>
                        </Grid>
                     </Grid>
                  </Paper>
               </Grid>

               {/* Expenses List */}
               <Grid item xs={12}>
                  <Paper sx={{ p: 3 }}>
                     <Typography variant='h6' gutterBottom>
                        Expense History
                     </Typography>
                     {expenses.length === 0 ? (
                        <Typography color='text.secondary' align='center' sx={{ py: 3 }}>
                           No expenses recorded yet
                        </Typography>
                     ) : (
                        <List>
                           {expenses.map((expense) => (
                              <ListItem
                                 key={expense.id}
                                 secondaryAction={
                                    <IconButton
                                       edge='end'
                                       onClick={() => handleDeleteExpense(expense.id)}
                                    >
                                       <DeleteIcon />
                                    </IconButton>
                                 }
                              >
                                 <ListItemText
                                    primary={expense.description}
                                    secondary={`${formatCurrency(expense.amount)} - ${formatDateRange(expense.date, expense.date)}`}
                                 />
                              </ListItem>
                           ))}
                        </List>
                     )}
                  </Paper>
               </Grid>
            </Grid>
         ),
      },
      {
         label: 'Marketing',
         icon: <MarketingIcon />,
         content: marketingTabContent,
      },
      {
         label: 'Analytics',
         icon: <AnalyticsIcon />,
         content: (
            <Box sx={{ p: 3 }}>
               <EventAnalyticsTab
                  organizationId={id || ''}
                  eventId={eventId || ''}
                  eventTitle={event?.title || ''}
               />
            </Box>
         ),
      },
   ];

   console.log('event', event);

   return (
      <Container maxWidth='lg' sx={{ mt: 4, mb: 4 }}>
         {/* Top Navigation Bar */}
         <Paper sx={{ p: 2, mb: 3 }}>
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
               <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(`/organizations/${id}/events`)}
               >
                  Back to Events
               </Button>
               <Stack direction='row' spacing={1}>
                  <Button
                     variant='contained'
                     color='primary'
                     startIcon={<ShareIcon />}
                     onClick={() => setShareDialogOpen(true)}
                  >
                     Share
                  </Button>
                  <Button variant='outlined' startIcon={<EditIcon />} onClick={handleEditEvent}>
                     Edit
                  </Button>
                  <Button
                     variant='outlined'
                     color='error'
                     startIcon={<DeleteIcon />}
                     onClick={handleDeleteDialogOpen}
                  >
                     Delete
                  </Button>
               </Stack>
            </Stack>
         </Paper>

         {/* Event Header with Cover Image */}
         <Paper sx={{ mb: 3, overflow: 'hidden' }}>
            {event.coverPhotoUrl ? (
               <Box
                  sx={{
                     height: 300,
                     width: '100%',
                     bgcolor: 'primary.main',
                     backgroundImage: `url(${event.coverPhotoUrl})`,
                     backgroundSize: 'cover',
                     backgroundPosition: 'center',
                     cursor: 'pointer',
                  }}
                  onClick={() => setPhotoDialogOpen(true)}
               >
                  <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
                     {event?.startDate && (
                        <Chip
                           label={`${event?.startDate.toLocaleDateString()}`}
                           color='primary'
                           size='small'
                        />
                     )}{' '}
                     -{' '}
                     {event?.endDate && (
                        <Chip
                           label={`${event?.endDate.toLocaleDateString()}`}
                           color='primary'
                           size='small'
                        />
                     )}
                  </Box>
               </Box>
            ) : (
               <Box
                  sx={{
                     height: 200,
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     bgcolor: 'primary.light',
                  }}
               >
                  <EventIcon sx={{ fontSize: 80, color: 'white', opacity: 0.7 }} />
               </Box>
            )}
            <Box sx={{ p: 3 }}>
               <Typography variant='overline' color='text.secondary'>
                  {activeOrganization?.name || 'Organization'} Event
               </Typography>
               <Typography variant='h4' component='h1' gutterBottom>
                  {event.title}
               </Typography>
            
            </Box>
         </Paper>

         {/* Quick Stats Cards */}
         <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
               <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                     <TicketIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                     <Typography variant='h4' gutterBottom>
                        {event.ticketsSold}/{event.maxCapacity}
                     </Typography>
                     <Typography variant='subtitle1' color='text.secondary'>
                        Tickets Sold
                     </Typography>
                     <LinearProgress
                        variant='determinate'
                        value={(event.ticketsSold / event.maxCapacity) * 100}
                        sx={{ mt: 2, height: 8, borderRadius: 4 }}
                     />
                  </CardContent>
               </Card>
            </Grid>
            <Grid item xs={12} md={4}>
               <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                     <MonetizationIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                     <Typography variant='h4' gutterBottom>
                        ${event.revenue || 0}
                     </Typography>
                     <Typography variant='subtitle1' color='text.secondary'>
                        Total Revenue
                     </Typography>
                     <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
                        {event.isPaid ? `$${event.price} per ticket` : 'Free event'}
                     </Typography>
                  </CardContent>
               </Card>
            </Grid>
            <Grid item xs={12} md={4}>
               <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                     <CheckInIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                     <Typography variant='h4' gutterBottom>
                        {checkedInCount}/{event.ticketsSold}
                     </Typography>
                     <Typography variant='subtitle1' color='text.secondary'>
                        Checked In
                     </Typography>
                     <LinearProgress
                        variant='determinate'
                        value={checkedInPercentage}
                        sx={{ mt: 2, height: 8, borderRadius: 4 }}
                     />
                  </CardContent>
               </Card>
            </Grid>
         </Grid>

         {/* Main Content Tabs */}
         <Card>
            <Tabs
               value={tabValue}
               onChange={handleTabChange}
               variant='scrollable'
               scrollButtons='auto'
               sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
               {tabs.map((tab, index) => (
                  <Tab key={index} icon={tab.icon} label={tab.label} {...a11yProps(index)} />
               ))}
            </Tabs>

            {/* Tab content */}
            {tabs.map((tab) => (
               <TabPanel key={tab.label} value={tabValue} index={tabs.indexOf(tab)}>
                  {tab.content}
               </TabPanel>
            ))}
         </Card>

         {/* Keep existing dialogs */}
         {/* ... existing dialogs ... */}

         <ShareDialog
            open={shareDialogOpen}
            onClose={() => setShareDialogOpen(false)}
            eventId={event?.id || ''}
            shareUrl={getShareUrl()}
            eventTitle={event?.title || ''}
         />

         {/* Add QR Code Modal */}
         {event && (
            <QRCodeModal
               open={qrCodeModalOpen}
               onClose={() => setQrCodeModalOpen(false)}
               eventId={eventId || ''}
               organizationId={id || ''}
               eventTitle={event.title}
            />
         )}

         {/* Preview Modal */}
         <Dialog
            open={previewOpen}
            onClose={handlePreviewClose}
            maxWidth='md'
            fullWidth
            PaperProps={{
               sx: {
                  height: '90vh',
                  maxHeight: '90vh',
               },
            }}
         >
            <DialogTitle>
               <Box
                  sx={{
                     display: 'flex',
                     justifyContent: 'space-between',
                     alignItems: 'center',
                     borderBottom: 1,
                     borderColor: 'divider',
                     bgcolor: 'background.paper',
                     position: 'sticky',
                     top: 0,
                     zIndex: 1,
                     width: '100%',
                  }}
               >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                     <VisibilityIcon />
                     <Typography>Public Event Preview</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                     {event?.publicLinkId && (
                        <Button
                           size='small'
                           startIcon={<OpenInNewIcon />}
                           onClick={() => window.open(getShareUrl(), '_blank')}
                        >
                           Open Public Link
                        </Button>
                     )}
                     <IconButton size='small' onClick={handlePreviewClose}>
                        <CloseIcon />
                     </IconButton>
                  </Box>
               </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
               <PublicEventPreview />
            </DialogContent>
         </Dialog>

         {/* Banner Dialog */}
         <Dialog
            open={bannerDialogOpen}
            onClose={() => setBannerDialogOpen(false)}
            maxWidth='md'
            fullWidth
         >
            <DialogTitle>
               {selectedBanner
                  ? 'Edit Asset'
                  : `Create New ${
                       currentAssetType === 'banner'
                          ? 'Banner'
                          : currentAssetType === 'social'
                            ? 'Social Media Graphic'
                            : 'Promotional Material'
                    }`}
            </DialogTitle>
            <DialogContent dividers>
               <TextField
                  label='Title'
                  fullWidth
                  value={
                     selectedBanner?.title ||
                     `${event?.title || ''} ${
                        currentAssetType === 'banner'
                           ? 'Banner'
                           : currentAssetType === 'social'
                             ? 'Social Graphic'
                             : 'Flyer'
                     }`
                  }
                  margin='normal'
               />

               <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                     <FormControl fullWidth>
                        <InputLabel id='asset-type-label'>Asset Type</InputLabel>
                        <Select
                           labelId='asset-type-label'
                           id='asset-type'
                           value={currentAssetType}
                           label='Asset Type'
                           onChange={(e) => setCurrentAssetType(e.target.value as AssetType)}
                        >
                           <MenuItem value='banner'>Event Banner</MenuItem>
                           <MenuItem value='social'>Social Media Graphic</MenuItem>
                           <MenuItem value='flyer'>Promotional Flyer</MenuItem>
                        </Select>
                     </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                     <FormControl fullWidth>
                        <InputLabel id='style-label'>Visual Style</InputLabel>
                        <Select
                           labelId='style-label'
                           id='style'
                           value={imageStyle}
                           label='Visual Style'
                           onChange={(e) => setImageStyle(e.target.value as DesignStyle)}
                        >
                           <MenuItem value='modern'>Modern</MenuItem>
                           <MenuItem value='minimalist'>Minimalist</MenuItem>
                           <MenuItem value='vibrant'>Vibrant & Colorful</MenuItem>
                           <MenuItem value='corporate'>Corporate</MenuItem>
                           <MenuItem value='artistic'>Artistic</MenuItem>
                           <MenuItem value='vintage'>Vintage/Retro</MenuItem>
                        </Select>
                     </FormControl>
                  </Grid>
               </Grid>

               <TextField
                  label='AI Prompt'
                  fullWidth
                  multiline
                  rows={3}
                  value={imagePrompt}
                  onChange={(e) => setImagePrompt(e.target.value)}
                  margin='normal'
                  placeholder='Describe how you want your visual to look, or leave empty for auto-generation based on event details...'
                  helperText='You can customize the prompt or leave it empty to use event details'
               />

               <TextField
                  label='Description'
                  fullWidth
                  multiline
                  rows={2}
                  value={selectedBanner?.description || bannerDescription}
                  onChange={(e) => {
                     if (selectedBanner) {
                        setSelectedBanner({ ...selectedBanner, description: e.target.value });
                     } else {
                        setBannerDescription(e.target.value);
                     }
                  }}
                  margin='normal'
                  placeholder='Add a description for this asset...'
               />

               <Box sx={{ my: 3, textAlign: 'center' }}>
                  {generatingImage ? (
                     <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress />
                        <Typography sx={{ mt: 2 }}>
                           Generating your visual asset with Stability AI...
                        </Typography>
                     </Box>
                  ) : generatedImageUrl || selectedBanner?.imageUrl ? (
                     <>
                        <img
                           src={generatedImageUrl || selectedBanner?.imageUrl}
                           alt='Visual Asset Preview'
                           style={{
                              maxWidth: '100%',
                              maxHeight: '400px',
                              objectFit: 'contain',
                              border: '1px solid #eee',
                              borderRadius: '4px',
                           }}
                        />
                        <Typography
                           variant='caption'
                           display='block'
                           sx={{ mt: 1, color: 'text.secondary' }}
                        >
                           Generated with Stability AI for {event?.title}
                        </Typography>
                     </>
                  ) : (
                     <Paper
                        sx={{
                           height: 200,
                           width: '100%',
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           bgcolor: 'grey.100',
                        }}
                     >
                        <Typography color='text.secondary'>
                           Preview will appear here after generation
                        </Typography>
                     </Paper>
                  )}
               </Box>

               <Button
                  fullWidth
                  variant='outlined'
                  startIcon={<AutoAwesomeIcon />}
                  sx={{ mt: 2 }}
                  onClick={generateImageWithStabilityAI}
                  disabled={generatingImage}
               >
                  {generatingImage ? 'Generating...' : 'Generate with Stability AI'}
               </Button>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setBannerDialogOpen(false)}>Cancel</Button>
               <Button
                  variant='contained'
                  onClick={saveGeneratedAsset}
                  disabled={!generatedImageUrl && !selectedBanner?.imageUrl}
               >
                  Save Asset
               </Button>
            </DialogActions>
         </Dialog>

         {/* Embed Code Modal */}
         <Dialog
            open={embedCodeModalOpen}
            onClose={() => setEmbedCodeModalOpen(false)}
            maxWidth='md'
            fullWidth
         >
            <DialogTitle>Event Embed Code</DialogTitle>
            <DialogContent dividers>
               <EventEmbedGenerator
                  eventId={eventId || ''}
                  publicLinkId={event?.publicLinkId}
                  organizationId={id || ''}
                  eventTitle={event?.title || ''}
               />
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setEmbedCodeModalOpen(false)}>Close</Button>
            </DialogActions>
         </Dialog>
      </Container>
   );
};

export default OrganizationEventDetail; 